import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import LegalLayout from '../legal/LegalLayout';
import { PrivacyShieldIllustration } from '../legal/illustrations';
import { authHeader, isLoggedIn } from '../auth/auth';

// ─────────────────────────────────────────────────────────────────────────────
// Report a profile or an event. Mirrors the backend ReportController:
//   POST /api/reports  (auth required)
//   body: { report_ref: 1=user|2=event, reported_object_id, report_title, comment }
// The reporting user is always the authenticated user (set server-side).
// ─────────────────────────────────────────────────────────────────────────────

const API = 'https://maineventbackend.seuytel.com/api/';

// report_ref values (match Report::REF_USER / REF_EVENT on the backend).
const REF_USER = 1;
const REF_EVENT = 2;

// report_title reason codes (frontend mapping → stored as integer).
const REASONS = [
    { code: 1, label: 'Belästigung oder Mobbing' },
    { code: 2, label: 'Spam oder Betrug' },
    { code: 3, label: 'Unangemessene oder illegale Inhalte' },
    { code: 4, label: 'Fake-Profil / Identitätsmissbrauch' },
    { code: 5, label: 'Sonstiges' },
];

type Status = 'idle' | 'sending' | 'done' | 'error';

export default function ReportProfile() {
    const [params] = useSearchParams();

    const initialRef = params.get('ref') === '2' || params.get('event') ? REF_EVENT : REF_USER;
    const initialId = params.get('id') || params.get('user') || params.get('event') || '';

    const [ref, setRef] = useState<number>(initialRef);
    const [objectToken, setObjectToken] = useState<string>(initialId);
    const [reason, setReason] = useState<number>(REASONS[0].code);
    const [comment, setComment] = useState('');
    const [status, setStatus] = useState<Status>('idle');
    const [error, setError] = useState('');

    const loggedIn = isLoggedIn();

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const token = objectToken.trim();
        if (!token) {
            setStatus('error');
            setError('Bitte geben Sie einen gültigen Token des zu meldenden Profils oder Events an.');
            return;
        }

        setStatus('sending');
        try {
            const res = await fetch(`${API}reports`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    ...authHeader(),
                },
                body: JSON.stringify({
                    report_ref: ref,
                    // The internal id is not exposed — send the public token; the
                    // backend resolves it to the reported object's id.
                    reported_object_id: token,
                    report_title: reason,
                    comment: comment.trim() || null,
                }),
            });

            if (res.status === 401) {
                setStatus('error');
                setError('Bitte melden Sie sich an, um eine Meldung abzusenden.');
                return;
            }

            const data = await res.json().catch(() => ({}));
            if (!res.ok || data?.status === 'error') {
                setStatus('error');
                setError(data?.message ? String(data.message) : 'Die Meldung konnte nicht gesendet werden.');
                return;
            }

            setStatus('done');
        } catch {
            setStatus('error');
            setError('Netzwerkfehler. Bitte versuchen Sie es später erneut.');
        }
    };

    return (
        <LegalLayout
            eyebrow="Melden"
            title="Profil oder Event melden"
            intro="Sie können ein Nutzerprofil oder ein Event melden, das gegen unsere Richtlinien verstößt. Unser Team prüft jede Meldung vertraulich."
            art={<PrivacyShieldIllustration />}
        >
            {!loggedIn ? (
                <div className="se-alert se-alert-info">
                    Zum Absenden einer Meldung ist eine Anmeldung erforderlich.{' '}
                    <Link to="/login">Jetzt anmelden</Link>.
                </div>
            ) : status === 'done' ? (
                <div className="se-alert se-alert-success">
                    <strong>Danke für Ihre Meldung.</strong> Wir haben sie erhalten und prüfen sie. Bei Rückfragen meldet
                    sich unser Support unter <a href="mailto:support@mainevents.seuytel.com">support@mainevents.seuytel.com</a>.
                </div>
            ) : (
                <form className="se-form-card" onSubmit={submit}>
                    <div className="se-field">
                        <label className="se-label">Was möchten Sie melden?</label>
                        <div className="se-radio-row">
                            <label className={`se-radio${ref === REF_USER ? ' se-active' : ''}`}>
                                <input
                                    type="radio"
                                    name="ref"
                                    checked={ref === REF_USER}
                                    onChange={() => setRef(REF_USER)}
                                />
                                Nutzerprofil
                            </label>
                            <label className={`se-radio${ref === REF_EVENT ? ' se-active' : ''}`}>
                                <input
                                    type="radio"
                                    name="ref"
                                    checked={ref === REF_EVENT}
                                    onChange={() => setRef(REF_EVENT)}
                                />
                                Event
                            </label>
                        </div>
                    </div>

                    <div className="se-field">
                        <label className="se-label" htmlFor="rep-id">
                            {ref === REF_USER ? 'Token des Nutzerprofils' : 'Token des Events'}
                        </label>
                        <input
                            id="rep-id"
                            className="se-input"
                            type="text"
                            value={objectToken}
                            onChange={(e) => setObjectToken(e.target.value)}
                            placeholder="z. B. a1b2c3d4…"
                            required
                        />
                        <span className="se-help">
                            Den Token finden Sie in der App auf dem Profil bzw. der Event-Seite (Teilen → Link).
                        </span>
                    </div>

                    <div className="se-field">
                        <label className="se-label" htmlFor="rep-reason">Grund</label>
                        <select
                            id="rep-reason"
                            className="se-input"
                            value={reason}
                            onChange={(e) => setReason(parseInt(e.target.value, 10))}
                        >
                            {REASONS.map((r) => (
                                <option key={r.code} value={r.code}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="se-field">
                        <label className="se-label" htmlFor="rep-comment">Beschreibung (optional)</label>
                        <textarea
                            id="rep-comment"
                            className="se-input se-textarea"
                            rows={5}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Beschreiben Sie kurz, was passiert ist…"
                        />
                    </div>

                    {status === 'error' && <div className="se-alert se-alert-error">{error}</div>}

                    <button type="submit" className="se-btn-primary" disabled={status === 'sending'}>
                        {status === 'sending' ? 'Wird gesendet…' : 'Meldung absenden'}
                    </button>
                </form>
            )}

            <p className="se-callout" style={{ marginTop: 24 }}>
                Bei akuter Gefahr oder Straftaten wenden Sie sich bitte zusätzlich an die zuständigen Behörden.
            </p>
        </LegalLayout>
    );
}
